import { formatAsDate } from '../../../config/dayjsConfig'
import { Column } from '../../../redux/context/periodGroups/hooks'
import { ReportDataType } from '../../../redux/context/reports/types'
import { store } from '../../../redux/store'
import { vouchersEnabled } from '../../../routes/utils'
import { AccountMapRule } from '../../../types/accountMapRule/AccountMapRule'
import { ReportTableRow } from '../../../components/Table/types'
import { VoucherQueryParams, AccountCondition } from '../../../pages/reporting/vouchers/types'
import { currentUserSelector } from '../../../redux/session/currentUser/selectors'
import { queryClient } from '../../../services/api/queryClient'
import accountMapQueryKeys from '../../accountMap/queries/accountMapQueryKeys'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { groupRulesByStatementRowId } from '../../accountMap/utils/accountMapUtils'
import { Company } from '../../../types/company/Company'

/**
 * Get account conditions for a statement row by its ID (for heading rows only)
 * Returns an array of account codes or ranges
 * For account rows, use the account code directly instead
 */
export const getStatementRowAccounts = (statementRowId: number): AccountCondition[] => {
  // Get companyId from Redux store
  const state = store.getState() as any
  const companyId = contextCompanyIdSelector(state)

  if (!companyId) {
    return []
  }

  // Get account map rules from TanStack Query cache
  const cachedRules = queryClient.getQueryData<AccountMapRule[]>(accountMapQueryKeys.list(companyId))

  if (!cachedRules) {
    return []
  }

  // Group rules by statementRowId
  const accountMapRules = groupRulesByStatementRowId(cachedRules)
  const rules = accountMapRules[statementRowId] as AccountMapRule[] | undefined

  if (!rules || rules.length === 0) {
    return []
  }

  // Map each rule to an AccountCondition (either a single number or a range object)
  return rules.map(({ from, to }) => {
    // If from and to are the same, return a single account code
    if (from === to) {
      return from
    }
    // Otherwise return a range object for multiple accounts
    return { from, to }
  })
}

export const showVoucherMenu = (row: ReportTableRow, column: Column) => {
  const state: any = store.getState()
  const { permissions, roles } = currentUserSelector(state)

  // Get accounts based on row type
  let accounts: AccountCondition[] = []
  if (typeof row?.code === 'number') {
    // Account row - use code directly
    accounts = [row.code]
  } else if (row.id) {
    // Heading row - look up accounts via statement row id
    accounts = getStatementRowAccounts(row.id)
  }

  if (column.dataType !== ReportDataType.actuals && column.dataType !== ReportDataType.forecast) return false
  if (row.rowType === 'budgetMenu') return false
  if (!vouchersEnabled()) return false
  if (accounts.length <= 0) return false

  if (roles.map((r: { label: string }) => r.label).includes('superuser')) return true
  if (!permissions?.map((p: { label: string }) => p.label).includes('read-vouchers')) return false

  return true
}

/**
 * Recursively collects all company IDs from a group.
 * The parent company ID is taken from company.groupId.
 * Elimination companies and G-prefixed group companies are skipped.
 */
export const collectGroupCompanyIds = (parentCompanyId: string, subsidiaries: Company[]): string[] => {
  const ids = new Set<string>([parentCompanyId])

  const process = (subs: Company[]) => {
    for (const sub of subs) {
      if (!sub.elimination) {
        if (sub.groupId) {
          // Subsidiary is itself a group — add its parent company ID
          ids.add(sub.groupId)
          // Recursively process its own subsidiaries
          if (sub.subsidiaries?.length) {
            process(sub.subsidiaries)
          }
        } else {
          ids.add(sub.id)
        }
      }
    }
  }

  process(subsidiaries)
  return Array.from(ids)
}

export const getRequestParams = (p: Partial<VoucherQueryParams>) => {
  return {
    ...p,
    invoicePartnerId: (p?.invoicePartnerId && p?.invoicePartnerId.length > 0 && p.invoicePartnerId) || undefined,
    from: p?.from ? formatAsDate(p.from) : undefined,
    until: p?.until ? formatAsDate(p.until) : undefined,
    invoiceNumber: p.invoiceNumber || undefined,
    invoiceReference: p?.invoiceReference && p.invoiceReference.length > 0 ? p.invoiceReference : undefined,
    companyIds: p?.companyIds && p.companyIds.length > 0 ? p.companyIds : undefined,
    transactionDesc: p.transactionDesc || undefined,
    voucherDesc: p.voucherDesc || undefined,
    guid: p.guid || undefined,
    accounts: (p?.accounts || [])?.length > 0 ? p.accounts : undefined,
    voucherType: (p?.voucherType && p?.voucherType.length > 0 && p.voucherType) || undefined
  }
}
